import { Button } from "@mui/joy";
import { useCallback, useEffect, useState } from "react";
import { ProgramContentType } from "data/BlockComponent";
import { setProgramContentData } from "logic/logics";
import { sendMessage } from "utils/webview"; // appLog 사용하지 않으면 제거
import useGetEventFromApp from "hooks/useGetEventFromApp";
import * as _ from "lodash";

type PlayerStatus =
  | "idle"
  | "loading"
  | "playing"
  | "paused" // 네이티브 'stopped' 상태도 이 상태로 처리
  | "finished"
  | "error"
  | "info"; // 네이티브에서 추가 정보 전달 시 사용 가능

export default function AudioButtonWebviewLegacy({
  audioInstanceKey,
  audioUrl,
  initialTime,
  setData,
  blockIndex,
  lineIndex,
  cellIndex,
  isCoach,
  title,
  artist,
  artwork,
  extraAudioUrl, // 현재 로직에서 직접 사용되진 않지만, props로 유지
  extraLoopNumber = 0, // 현재 로직에서 직접 사용되진 않지만, props로 유지
  readonly,
}: {
  audioInstanceKey: string;
  audioUrl: string;
  initialTime: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  blockIndex: number;
  lineIndex: number;
  cellIndex: number;
  isCoach: boolean;
  title?: string;
  artist?: string;
  artwork?: string;
  extraAudioUrl?: string;
  extraLoopNumber?: number; // 안씀
  readonly: boolean;
}) {
  const [playerStatus, setPlayerStatus] = useState<PlayerStatus>("idle");
  const [currentTime, setCurrentTime] = useState<number>(initialTime);
  const [duration, setDuration] = useState<number>(0);

  useEffect(() => {
    setCurrentTime(initialTime);
    if (initialTime > 0 && playerStatus === "idle") {
      if (duration > 0 && initialTime - 5 >= duration) {
        setPlayerStatus("finished");
      } else {
        setPlayerStatus("paused");
      }
    }
  }, [initialTime, playerStatus, duration]);

  useEffect(() => {
    if (playerStatus === "finished") {
      if (duration > 0 && currentTime > 0) {
        setCurrentTime(duration);
        handleAudioFinished();
      }
    }
  }, [playerStatus, duration, currentTime]);

  const requestLoadAndPlay = useCallback(() => {
    const timeToStart = playerStatus === "error" ? 0 : currentTime;
    if (playerStatus === "error") {
      setCurrentTime(0);
    }
    sendMessage(
      JSON.stringify({
        event: "audioAction",
        params: {
          action: "loadAndPlay",
          src: audioUrl,
          initialTime: timeToStart,
          audioInstanceKey: audioInstanceKey,
          ...(title && { title }),
          ...(artist && { artist }),
          ...(artwork && { artwork }),
        },
      })
    );
  }, [
    audioUrl,
    currentTime,
    playerStatus,
    audioInstanceKey,
    title,
    artist,
    artwork,
  ]);

  const requestPause = useCallback(() => {
    sendMessage(
      JSON.stringify({
        event: "audioAction",
        params: { action: "pause", audioInstanceKey: audioInstanceKey },
      })
    );
  }, [audioInstanceKey]);

  const requestResume = useCallback(() => {
    sendMessage(
      JSON.stringify({
        event: "audioAction",
        params: {
          action: "resume",
          src: audioUrl,
          initialTime: currentTime,
          audioInstanceKey: audioInstanceKey,
          ...(title && { title }),
          ...(artist && { artist }),
          ...(artwork && { artwork }),
        },
      })
    );
  }, [audioUrl, currentTime, audioInstanceKey, title, artist, artwork]);

  // 컴포넌트 언마운트 시 호출될 함수
  const requestGracefulStopOnUnmount = useCallback(() => {
    sendMessage(
      JSON.stringify({
        event: "audioAction",
        params: {
          action: "gracefulStop",
          audioInstanceKey: audioInstanceKey,
        },
      })
    );
  }, [audioInstanceKey]);

  const parsedMessageFromApp = useGetEventFromApp();
  useEffect(() => {
    if (
      parsedMessageFromApp?.event === "audioPlayer" &&
      parsedMessageFromApp.params
    ) {
      const {
        status: statusFromApp,
        currentTime: timeFromApp,
        duration: durationFromApp,
        errorCode, // 필요시 사용
        errorMessage, // 필요시 사용
        // message, // info 상태일 때 사용
        audioInstanceKey: instanceKeyFromApp,
      } = parsedMessageFromApp.params;

      if (instanceKeyFromApp !== audioInstanceKey) {
        return; // 이 컴포넌트와 관련된 메시지가 아님
      }

      if (typeof durationFromApp === "number" && durationFromApp > 0) {
        setDuration(durationFromApp);
      }

      if (typeof timeFromApp === "number" && timeFromApp >= 0) {
        if (statusFromApp === "stopped" && timeFromApp === 0) {
          // stopped 인 경우 currentTime 이 0으로 reset 되는걸 방지
        } else {
          setCurrentTime(timeFromApp);
        }
      }

      // 상태 업데이트
      if (statusFromApp === "stopped") {
        setPlayerStatus("paused");
      } else if (statusFromApp === "unmounted") {
        // 이 컴포넌트가 보낸 언마운트 메시지를 다시 받을 일은 없지만, 방어적으로 둠
        return;
      } else if (statusFromApp && statusFromApp !== "info") {
        // info 상태는 playerStatus를 변경하지 않음
        setPlayerStatus(statusFromApp as PlayerStatus);
      }

      // 특정 상태 후처리
      if (statusFromApp === "error") {
        console.error(
          `[AudioButtonWebview ${audioInstanceKey}] Audio Error from Native:`,
          errorCode,
          errorMessage
        );
      }

      if (statusFromApp === "finished") {
        const finalTime =
          typeof durationFromApp === "number" && durationFromApp > 0
            ? durationFromApp
            : duration;
        setCurrentTime(Math.floor(finalTime));
        handleAudioFinished();
      }
      // if (statusFromApp === "info" && message) {
      //   console.log(`[AudioButtonWebview ${audioInstanceKey}] Info from Native:`, message);
      //   // 필요시 사용자에게 알림 (예: 토스트 메시지)
      // }

      const isEnded =
        duration > 0 && currentTime > 0 && duration - 30 <= currentTime;

      // 5초 단위 데이터 저장 로직
      if (
        (statusFromApp === "playing" || statusFromApp === "paused") &&
        typeof timeFromApp === "number" &&
        timeFromApp % 5 === 0
      ) {
        setProgramContentData({
          setData,
          blockIndex,
          lineIndex,
          cellIndex,
          newlyAddedData: {
            currentTime: timeFromApp,
            ...(timeFromApp > 0 && {
              id: "meditation_time",
              value: `${Math.floor(((timeFromApp || 0) + 5) /*보정값 5초*/ / 60)}`,
            }),
            ...(isEnded && {
              isEnded: true,
            }),
          },
        });
      }
    }
  }, [
    parsedMessageFromApp,
    audioInstanceKey,
    setData,
    blockIndex,
    lineIndex,
    cellIndex,
    duration,
  ]);

  const handleAudioFinished = () => {
    if (!isCoach) {
      setProgramContentData({
        setData,
        blockIndex,
        lineIndex,
        cellIndex,
        newlyAddedData: {
          currentTime: currentTime,
          ...(currentTime > 0 && {
            id: "meditation_time",
            value: `${Math.floor(((currentTime || 0) + 5) /*보정값 5초*/ / 60)}`,
          }),
          isEnded: true,
        },
      });
    }
  };

  const setAudioEnded = useCallback(() => {
    setData((data) => {
      if (data) {
        const data_temp = _.cloneDeep(data);
        const currentContent =
          data_temp[blockIndex].lines[lineIndex][cellIndex].content || {};

        data_temp[blockIndex].lines[lineIndex][cellIndex].content = {
          ...currentContent,
          isEnded: true,
        };
        if (!_.isEqual(data, data_temp)) {
          return data_temp;
        }
        return data;
      }
      return data;
    });
  }, [setData, blockIndex, lineIndex, cellIndex]);

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      if (!isCoach) setAudioEnded();
    }
  }, [currentTime, isCoach, setAudioEnded]);

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  function parseSecondsToFormat(seconds: number) {
    if (isNaN(seconds) || seconds < 0) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
  }

  const handleButtonClick = () => {
    // console.log(`[AudioButtonWebview ${audioInstanceKey}] Button clicked. Status: ${playerStatus}, CurrentTime: ${currentTime}`);
    switch (playerStatus) {
      case "idle":
      case "error":
        requestLoadAndPlay();
        break;
      case "finished":
        // TODO extraLoop
        break;
      case "loading":
        break;
      case "playing":
        requestPause();
        break;
      case "paused": // stopped + paused
        requestResume();
        break;
      default:
        break;
    }
  };

  const getButtonText = () => {
    switch (playerStatus) {
      case "idle":
        return currentTime > 0 && duration > 0
          ? `이어서 재생하기`
          : "음성 안내 재생하기";
      case "loading":
        return "로딩 중...";
      case "playing":
        if (
          duration > 0 &&
          currentTime >= duration - 1 &&
          currentTime <= duration
        ) {
          return `${parseSecondsToFormat(duration)} / ${parseSecondsToFormat(duration)}`;
        }
        return `${parseSecondsToFormat(currentTime)} / ${parseSecondsToFormat(duration)}`;
      case "paused":
        return currentTime > 0 && duration > 0
          ? `이어서 재생하기`
          : "음성 안내 재생하기";
      case "finished":
        return `음성 안내 종료`;
      case "error":
        return "음성 안내 재생하기";
      default:
        return "음성 안내 재생하기";
    }
  };

  // 컴포넌트 언마운트 시 네이티브에 알림
  useEffect(() => {
    return () => {
      requestGracefulStopOnUnmount();
    };
  }, [requestGracefulStopOnUnmount]);

  return (
    <div>
      <Button
        fullWidth
        onClick={handleButtonClick}
        variant={playerStatus === "playing" ? "Outlined" : "Solid"}
        size={"lg"}
        disabled={readonly || playerStatus === "loading"}
      >
        {getButtonText()}
      </Button>
    </div>
  );
}
